import axios from "axios";
import { AppRoutes, backend_url } from "../constants";
import { AXIOS_CONFIG } from "../utils/api";
import { Endpoints } from "@/utils/api/endpoints";
import { emitDebugEvent } from "@/utils/debug/eventIntegration";

export const AXIOS_BYPASS_ENDPOINTS = [
  Endpoints.authentication.login,
  Endpoints.authentication.logout,
  Endpoints.authentication.refresh,
  Endpoints.branding,
  Endpoints.authentication.tokenCheck,
];

const extractBillingHardStopCode = (data: unknown): string | undefined => {
  if (!data || typeof data !== "object") return undefined;
  const d = data as Record<string, unknown>;
  if (typeof d.code === "string") return d.code;
  const detail = d.detail;
  if (detail && typeof detail === "object" && !Array.isArray(detail)) {
    const dd = detail as Record<string, unknown>;
    if (typeof dd.code === "string") return dd.code;
  }
  return undefined;
};

const BILLING_HARD_STOP_CODES = new Set([
  "managed_by_service_bureau",
  "credit_limit_reached",
  "credit_access_disabled_by_bureau",
  "managed_business_allocation_not_supported",
]);

let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: any) => void;
  reject: (reason?: any) => void;
}> = [];

// Check if we're in the middle of logging out (persists across page reloads)
const isLoggingOut = () => {
  return sessionStorage.getItem("isLoggingOut") === "true";
};

// Export function to set logout state
export const setLoggingOut = (value: boolean) => {
  if (value) {
    sessionStorage.setItem("isLoggingOut", "true");
  } else {
    sessionStorage.removeItem("isLoggingOut");
  }
};

// Clear logout flag on successful navigation to login page
if (window.location.pathname.includes("/login")) {
  // Give a short delay to allow any in-flight requests to complete
  setTimeout(() => {
    sessionStorage.removeItem("isLoggingOut");
  }, 1000);
}

function processQueue(error: any, response: any = null) {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(response);
    }
  });
  failedQueue = [];
}

axios.interceptors.request.use(
  (config) => {
    return config;
  },
  (error) => Promise.reject(error)
);

axios.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    // Ensure we don't enter an infinite loop for refresh attempts
    if (
      originalRequest?.url ===
      `${backend_url}/${Endpoints.authentication.refresh}`
    ) {
      return Promise.reject(error);
    }

    const billingCode = extractBillingHardStopCode(error.response?.data);
    if (billingCode && BILLING_HARD_STOP_CODES.has(billingCode)) {
      return Promise.reject(error);
    }

    let currentHostUrl = window.location.origin;
    if (currentHostUrl.includes("localhost")) {
      currentHostUrl = "https://smartwiz.link";
    }

    if (
      (error.response?.status === 401 || error.response?.status === 403) &&
      !AXIOS_BYPASS_ENDPOINTS.includes(
        originalRequest.url.replace(`${backend_url}/`, "")
      ) &&
      !originalRequest.url.startsWith(currentHostUrl) &&
      !isLoggingOut() // Don't auto-refresh during logout
    ) {
      if (isRefreshing) {
        // Queue the request until the refresh is done
        return new Promise(function (resolve, reject) {
          failedQueue.push({ resolve, reject });
        })
          .then(() => axios({ ...originalRequest, ...AXIOS_CONFIG }))
          .catch((err) => Promise.reject(err));
      }

      isRefreshing = true;
      return axios
        .post(
          `${backend_url}/${Endpoints.authentication.refresh}`,
          {},
          AXIOS_CONFIG
        )
        .then(() => {
          processQueue(null);
          return axios({ ...originalRequest, ...AXIOS_CONFIG });
        })
        .catch((refreshError) => {
          processQueue(refreshError, null);
          window.location.href = AppRoutes.logout;
          console.log("refreshError", refreshError);
          return Promise.reject(
            refreshError instanceof Error
              ? refreshError
              : new Error("Failed to refresh authentication")
          );
        })
        .finally(() => {
          isRefreshing = false;
        });
    }

    // Emit debug event for API errors (non-auth errors)
    if (
      error.response &&
      error.response.status !== 401 &&
      error.response.status !== 403
    ) {
      const endpoint =
        originalRequest?.url?.replace(`${backend_url}/`, "") || "unknown";
      const statusCode = error.response.status;
      const errorMessage =
        error.response.data?.message || error.message || "Unknown error";

      emitDebugEvent("error", "API_ERROR_RESPONSE", "web", {
        endpoint: endpoint.substring(0, 100),
        statusCode: statusCode.toString(),
        errorMessage: errorMessage.substring(0, 200),
        method: originalRequest?.method?.toUpperCase() || "UNKNOWN",
      });
    }

    return Promise.reject(
      error instanceof Error
        ? error
        : new Error(error?.message || "An unknown error occurred")
    );
  }
);
